import type { TonyteActivity, TonyteUser } from '../types';

// StudiZone campus maps are data, not code: every university is a
// Firestore doc in campusMaps/{id} (published with
// scripts/upsertCampusMap.mjs), so adding a university or a campus plan
// never needs an app update. Everything here parses those docs defensively
// — an unknown/partial doc degrades to "no plan" instead of crashing the map.

export type LatLng = { lat: number; lng: number };

export type CampusBuilding = {
  code: string;
  name: string;
  // Where the building is drawn on the plan overlay (pins go here).
  pin: LatLng;
  // The building's real position, used to match event coordinates.
  match: LatLng;
  aliases: string[];
};

export type CampusPlan = {
  imageUrl: string;
  bounds: { north: number; south: number; east: number; west: number };
  attribution: string;
};

export type Campus = {
  id: string;
  name: string;
  center: LatLng;
  zoom: number;
  matchKeywords: string[];
  plan: CampusPlan | null;
  buildings: CampusBuilding[];
};

export type CampusUniversity = {
  id: string;
  name: string;
  shortName: string;
  fullName: string;
  city: string;
  accentColor: string;
  order: number;
  domains: string[];
  matchKeywords: string[];
  searchTerms: string[];
  hostUids: string[];
  campuses: Campus[];
};

export type CampusMatch = {
  universityId: string;
  campusId: string;
  building: CampusBuilding;
};

const asString = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
const asStringList = (value: unknown) => (Array.isArray(value) ? value.map(asString).filter(Boolean) : []);
const asNumber = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

function asLatLng(value: unknown): LatLng | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const lat = asNumber(raw.lat);
  const lng = asNumber(raw.lng);
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

function parsePlan(value: unknown): CampusPlan | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const imageUrl = asString(raw.imageUrl);
  const b = (raw.bounds || {}) as Record<string, unknown>;
  const north = asNumber(b.north);
  const south = asNumber(b.south);
  const east = asNumber(b.east);
  const west = asNumber(b.west);
  if (!/^https:\/\//.test(imageUrl) || north === null || south === null || east === null || west === null) return null;
  if (north <= south || east <= west) return null;
  return { imageUrl, bounds: { north, south, east, west }, attribution: asString(raw.attribution) };
}

function parseBuilding(value: unknown): CampusBuilding | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const code = asString(raw.code);
  const pin = asLatLng(raw.pin);
  if (!code || !pin) return null;
  return {
    code,
    name: asString(raw.name) || code,
    pin,
    match: asLatLng(raw.match) || pin,
    aliases: asStringList(raw.aliases),
  };
}

function parseCampus(value: unknown): Campus | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const id = asString(raw.id);
  const center = asLatLng(raw.center);
  if (!id || !center) return null;
  return {
    id,
    name: asString(raw.name) || id,
    center,
    zoom: asNumber(raw.zoom) ?? 16,
    matchKeywords: asStringList(raw.matchKeywords).map(normalizeText),
    plan: parsePlan(raw.plan),
    buildings: (Array.isArray(raw.buildings) ? raw.buildings : []).map(parseBuilding).filter((b): b is CampusBuilding => Boolean(b)),
  };
}

export function parseCampusUniversity(id: string, data: Record<string, unknown>): CampusUniversity | null {
  if (data.active === false) return null;
  const campuses = (Array.isArray(data.campuses) ? data.campuses : []).map(parseCampus).filter((c): c is Campus => Boolean(c));
  const name = asString(data.name);
  if (!name || !campuses.length) return null;
  return {
    id,
    name,
    shortName: asString(data.shortName) || name,
    fullName: asString(data.fullName) || name,
    city: asString(data.city),
    accentColor: /^#[0-9a-f]{6}$/i.test(asString(data.accentColor)) ? asString(data.accentColor) : '#26D0CE',
    order: asNumber(data.order) ?? 100,
    domains: asStringList(data.domains).map(d => d.toLowerCase().replace(/^@+/, '')),
    matchKeywords: asStringList(data.matchKeywords).map(normalizeText),
    searchTerms: asStringList(data.searchTerms).map(normalizeText),
    hostUids: asStringList(data.hostUids),
    campuses,
  };
}

// Lowercase, ß -> ss, umlaut-insensitive, "Str." / "Strasse" -> "strasse",
// and hyphens/punctuation collapsed to single spaces, so "Andreas-Schubert-Str. 8"
// and "andreas schubert strasse 8" compare equal.
export function normalizeText(value: unknown): string {
  return String(value || '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/str\.(?=\s|\d|$)/g, 'strasse ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const containsPhrase = (haystack: string, phrase: string) =>
  Boolean(phrase) && ` ${haystack} `.includes(` ${phrase} `);

function metersBetween(a: LatLng, b: LatLng) {
  const dLat = (b.lat - a.lat) * 111320;
  const dLng = (b.lng - a.lng) * 111320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dLat, dLng);
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Building codes as they appear in real event locations: "Z-Gebäude",
// "PAB/Z-Gebäude", "Z110 (PAB)", "K 101", "Gebäude S", "Haus PN". Codes are
// matched case-sensitively and never inside a longer word.
function textMentionsBuildingCode(text: string, code: string) {
  const c = escapeRegExp(code);
  const letter = 'A-Za-zÄÖÜäöüß';
  const patterns = [
    new RegExp(`(^|[^${letter}])${c}\\s?-\\s?(Gebäude|Geb\\.?|Bau)`),
    new RegExp(`(^|[^${letter}])${c}\\s?\\d{2,4}[a-z]?(?![\\d${letter}])`),
    new RegExp(`(Gebäude|Geb\\.|Haus|Building)\\s+${c}(?![\\d${letter}])`),
  ];
  return patterns.some(pattern => pattern.test(text));
}

function activityText(activity: TonyteActivity) {
  const raw = activity as TonyteActivity & Record<string, unknown>;
  return [activity.locationName, activity.location, raw.address, raw.venueName, raw.venue]
    .map(asString)
    .filter(Boolean)
    .join(', ');
}

function activityContextText(activity: TonyteActivity) {
  const raw = activity as TonyteActivity & Record<string, unknown>;
  return [activityText(activity), activity.title, raw.organizerName, raw.hostName, ...(activity.hashtags || [])]
    .map(asString)
    .filter(Boolean)
    .join(' ');
}

function activityCoordinates(activity: TonyteActivity): LatLng | null {
  return asLatLng(activity.coordinates);
}

const isOnlineLocation = (text: string) => /^\s*(online|virtual|zoom|webinar)\b/i.test(text);

// Whether an event plausibly belongs to this campus at all — required before
// any building code/alias is trusted, so a "K 101" at some other venue never
// lands on HTW's K building.
function isRelevantToCampus(activity: TonyteActivity, university: CampusUniversity, campus: Campus, context: string) {
  const coords = activityCoordinates(activity);
  if (coords && campus.plan) {
    const { north, south, east, west } = campus.plan.bounds;
    if (coords.lat <= north && coords.lat >= south && coords.lng <= east && coords.lng >= west) return true;
  }
  const hostId = asString(activity.hostId) || asString(activity.ownerUid);
  if (hostId && university.hostUids.includes(hostId)) return true;
  const domains = (activity.audienceDomains || []).map(d => d.toLowerCase());
  if (domains.some(domain => university.domains.includes(domain))) return true;
  const normalized = normalizeText(context);
  return university.matchKeywords.some(keyword => containsPhrase(normalized, keyword));
}

function matchInCampus(activity: TonyteActivity, university: CampusUniversity, campus: Campus): CampusBuilding | null {
  const text = activityText(activity);
  if (!text && !activityCoordinates(activity)) return null;
  if (isOnlineLocation(text)) return null;
  const context = activityContextText(activity);
  if (!isRelevantToCampus(activity, university, campus, context)) return null;
  // A multi-campus university: campus keywords (e.g. "Pillnitz") steer the
  // event to that campus; without one, prefer campuses with no keywords.
  const normalizedContext = normalizeText(context);
  const otherCampusClaims = university.campuses.some(other =>
    other.id !== campus.id && other.matchKeywords.some(keyword => containsPhrase(normalizedContext, keyword)));
  const thisCampusClaims = campus.matchKeywords.some(keyword => containsPhrase(normalizedContext, keyword));
  if (otherCampusClaims && !thisCampusClaims) return null;

  const buildings = [...campus.buildings].sort((a, b) => b.code.length - a.code.length);
  const byCode = buildings.find(building => textMentionsBuildingCode(text, building.code));
  if (byCode) return byCode;

  const normalizedText = normalizeText(text);
  let best: { building: CampusBuilding; length: number } | null = null;
  campus.buildings.forEach(building => {
    building.aliases.forEach(alias => {
      const phrase = normalizeText(alias);
      if (phrase.length >= 3 && containsPhrase(normalizedText, phrase) && (!best || phrase.length > best.length)) {
        best = { building, length: phrase.length };
      }
    });
  });
  if (best) return (best as { building: CampusBuilding }).building;

  const coords = activityCoordinates(activity);
  if (coords) {
    let nearest: { building: CampusBuilding; distance: number } | null = null;
    campus.buildings.forEach(building => {
      const distance = metersBetween(coords, building.match);
      if (distance <= 35 && (!nearest || distance < nearest.distance)) nearest = { building, distance };
    });
    if (nearest) return (nearest as { building: CampusBuilding }).building;
  }
  return null;
}

// Explicit placement (a future partner-dashboard field) wins; otherwise the
// event's location text / coordinates are matched against each campus.
export function matchActivityToCampus(activity: TonyteActivity, universities: CampusUniversity[]): CampusMatch | null {
  const explicit = (activity as TonyteActivity & Record<string, unknown>).campusLocation as
    | { universityId?: unknown; campusId?: unknown; buildingCode?: unknown }
    | undefined;
  if (explicit && typeof explicit === 'object') {
    const university = universities.find(u => u.id === explicit.universityId);
    const campus = university?.campuses.find(c => c.id === explicit.campusId);
    const building = campus?.buildings.find(b => b.code === explicit.buildingCode);
    if (university && campus && building) return { universityId: university.id, campusId: campus.id, building };
  }
  for (const university of universities) {
    for (const campus of university.campuses) {
      const building = matchInCampus(activity, university, campus);
      if (building) return { universityId: university.id, campusId: campus.id, building };
    }
  }
  return null;
}

export function findUniversityForProfile(
  universities: CampusUniversity[],
  profile: Partial<Pick<TonyteUser, 'studentEmailDomain'>> | null | undefined,
): CampusUniversity | null {
  const domain = String(profile?.studentEmailDomain || '').trim().toLowerCase().replace(/^@+/, '');
  if (!domain) return null;
  return universities.find(u => u.domains.some(d => domain === d || domain.endsWith(`.${d}`))) || null;
}

export function searchUniversities(universities: CampusUniversity[], query: string): CampusUniversity[] {
  const q = normalizeText(query);
  const sorted = [...universities].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
  if (!q) return sorted;
  return sorted.filter(university => {
    const haystack = normalizeText([
      university.name,
      university.shortName,
      university.fullName,
      university.city,
      ...university.searchTerms,
      ...university.campuses.map(c => c.name),
    ].join(' '));
    return q.split(' ').every(token => haystack.includes(token));
  });
}
